import { combineReducers } from 'redux'
import request from 'utils/request'
import { modalActions } from 'store/modal'
import { loadingActions } from 'store/loader'
import { stringsActions } from 'store/strings'
import { actions as notyActions } from 'layouts/ErrorBox'
import { getParticipantErrors } from 'utils/participantValidation'

//
// Actions
//

const UPDATE_FIELD = 'UPDATE_FIELD'
const INITIALIZE_CREATE_PTP = 'INITIALIZE_CREATE_PTP'
const SET_LOCALES = 'SET_LOCALES'
const SET_PTP_COUNTRY = 'SET_PTP_COUNTRY'
const ADD_PTP_ERRORS = 'ADD_PTP_ERRORS'
const CLEAR_PTP_ERRORS = 'CLEAR_PTP_ERRORS'

//
// Action Creators
//

const addPtpErrors = errors => {
  return {
    type: ADD_PTP_ERRORS,
    errors,
  }
}

const clearPtpErrors = () => {
  return {
    type: CLEAR_PTP_ERRORS,
  }
}

const DISABLE_PARAM_TO_PARTICIPANT_KEY = {
  firstname: 'fname',
  lastname: 'lname',
}

const mapDisableParamToParticipantKey = paramKey => {
  if (DISABLE_PARAM_TO_PARTICIPANT_KEY[paramKey]) {
    return DISABLE_PARAM_TO_PARTICIPANT_KEY[paramKey]
  }
  if (paramKey.includes('firstname')) return 'fname'
  if (paramKey.includes('lastname')) return 'lname'
  return paramKey
}

const buildParticipantPayload = (
  participant,
  captchaToken,
  disabledFields,
  hiddenFields,
) => {
  const payload = { ...participant }
  const { phone } = payload

  payload.recaptcha_token = captchaToken
  payload.study_site_code = participant.siteCode
  payload.phone = phone || ''
  delete payload.siteCode
  delete payload.hasSmsConsent
  delete payload.countryDialCode
  delete payload.is_caregiver

  const omittedParamKeys = [...(disabledFields || []), ...(hiddenFields || [])]
  omittedParamKeys.forEach(paramKey => {
    delete payload[mapDisableParamToParticipantKey(paramKey)]
  })

  return payload
}

const saveToDatabase = (
  captchaToken,
  participant,
  cb,
  resetRecaptcha,
  disabledFields,
  hiddenFields,
) => {
  const participantToBeSaved = buildParticipantPayload(
    participant,
    captchaToken,
    disabledFields,
    hiddenFields,
  )
  return (dispatch, getState) => {
    const { strings } = getState()
    const ptpCopy = Object.assign({}, participantToBeSaved)
    const successMessage = strings.participant_create_success_self
    const existingPtpFailMessage =
      'This email is already registered in this study.' // string to check to ignore 400 error and continue to success screen
    return dispatch(
      request({
        authRequired: false,
        method: 'POST',
        successMessage,
        url: '/control/participants',
        failMessage: 'Error saving participant',
        body: JSON.stringify(ptpCopy),
        success: response => {
          delete participant.password
          cb()
        },
        fail: (res, message) => {
          if (res.status === 400) {
            if (message === existingPtpFailMessage) {
              cb()
              dispatch(
                notyActions.showSuccess({
                  text: successMessage,
                  timeout: 2000,
                }),
              )
              return
            }
            dispatch(
              notyActions.showError({
                text: message || strings.check_errors_self,
                timeout: 5000,
              }),
            )
            resetRecaptcha()
            return
          }
          if (res.status === 403) {
            const errors = {
              siteCode: strings.site_code_error_self,
            }
            dispatch(addPtpErrors(errors))
            dispatch(
              notyActions.showError({
                text: strings.check_errors_self,
              }),
            )
            resetRecaptcha()
          }
        },
        hasLoader: true,
      }),
    )
  }
}

const prepPhoneForSave = (phone, countryDialCode) => {
  let phoneToSave = phone.includes(countryDialCode) // this catches the case if the user enters the country dial code manually
    ? phone
    : `${countryDialCode}${phone}`
  phoneToSave = phoneToSave.replace(/[^0-9+]/g, '')
  return phoneToSave
}

const validateParticipantAndSave = (
  captchaToken,
  participant,
  cb,
  resetRecaptcha,
  disabledFields,
  hiddenFields,
) => {
  return (dispatch, getState) => {
    const { strings } = getState()
    const errors = getParticipantErrors(
      participant,
      captchaToken,
      strings,
      disabledFields,
    )
    const valid = Object.keys(errors).length === 0
    const { hasSmsConsent } = participant
    const participantCopy = { ...participant }
    const { phone, countryDialCode } = participantCopy
    const phoneDisabled = disabledFields?.includes('phone')
    participantCopy.phone = prepPhoneForSave(phone, countryDialCode)

    if (valid) {
      dispatch(clearPtpErrors())

      if (phoneDisabled || !hasSmsConsent) {
        delete participantCopy.phone
      }
      dispatch(
        saveToDatabase(
          captchaToken,
          participantCopy,
          cb,
          resetRecaptcha,
          disabledFields,
          hiddenFields,
        ),
      )
    } else {
      dispatch(addPtpErrors(errors))
      dispatch(
        notyActions.showError({
          text: strings.check_errors_self,
        }),
      )
    }
  }
}

const getLocaleOptions = locales => {
  const list = locales
    ? Object.keys(locales).map(key => {
        return {
          key,
          text: locales[key],
          value: key,
        }
      })
    : []
  return list
}

export const fetchLocales = () => {
  return dispatch => {
    const success = payload => {
      dispatch({
        type: SET_LOCALES,
        locales: getLocaleOptions(payload.locales),
      })
    }
    return dispatch(
      request({
        method: 'GET',
        url: '/control/locales',
        success,
        hasLoader: true,
        authRequired: false,
      }),
    )
  }
}

export const detectCountry = () => dispatch => {
  const success = payload =>
    dispatch({
      type: SET_PTP_COUNTRY,
      country: payload.countryCode,
    })
  return dispatch(
    request({
      authRequired: false,
      method: 'GET',
      url: 'http://ip-api.com/json',
      success,
      noAuthField: true,
      noContentType: true,
    }),
  )
}

function initializeEditPage(studyID, siteID, ptpID) {
  return dispatch => {
    dispatch(loadingActions.startLoader(true))
    return dispatch(fetchParticipant(studyID, siteID, ptpID)).then(() => {
      return dispatch(fetchPtpPayments(studyID, siteID, ptpID)).then(() => {
        return dispatch(fetchLocales()).then(() => {
          return dispatch(loadingActions.stopLoader(true))
        })
      })
    })
  }
}

function updateField(field, value) {
  return dispatch => {
    dispatch({
      type: UPDATE_FIELD,
      field,
      value,
    })
  }
}

function updateLocale(currentLocale, newLocale) {
  if (currentLocale === newLocale) return () => {}
  return dispatch => {
    dispatch(updateField('default_locale', newLocale))
    dispatch(stringsActions.onFetchApplangaStrings(newLocale))
  }
}

function triggerPasswordReset({ id, username }) {
  return dispatch => {
    dispatch(
      request({
        method: 'POST',
        url: `/control/participants/${id}/forgot_password`,
        body: JSON.stringify({ username }),
        successMessage: 'Reset password email sent to participant.',
      }),
    )
  }
}

function initializePage(fname = '', lname = '') {
  return dispatch => {
    // return dispatch(fetchLocales()).then(() => {
    return dispatch({
      type: INITIALIZE_CREATE_PTP,
      fname,
      lname,
    })
    // })
  }
}

function getInitialState(fname = '', lname = '') {
  return {
    fname,
    lname,
    email: '',
    default_locale: 'en_US',
    phone: '',
    date_of_birth: '',
    siteCode: '',
    is_self_sign_on: true,
    countryDialCode: '+1',
  }
}

//
// Reducers
//

const participant = (state = {}, action) => {
  let newState
  switch (action.type) {
    case INITIALIZE_CREATE_PTP: {
      return getInitialState(action.fname, action.lname)
    }
    case UPDATE_FIELD:
      newState = { ...state }
      newState[action.field] = action.value
      return newState
    default:
      return state
  }
}

const errors = (state = {}, action) => {
  const newState = { ...state }
  switch (action.type) {
    case INITIALIZE_CREATE_PTP:
    case CLEAR_PTP_ERRORS:
      return {}
    case ADD_PTP_ERRORS:
      return action.errors
    default:
      return newState
  }
}

export default combineReducers({
  participant,
  errors,
})

export const actions = {
  saveToDatabase: validateParticipantAndSave,
  updateField,
  updateLocale,
  initializePage,
  fetchLocales,
  triggerPasswordReset,
  openModal: modalActions.openModal,
  closeModal: modalActions.closeModal,
  initializeEditPage,
}



// WEBPACK FOOTER //
// ./src/store/newParticipant.js